import { playbookIdSchema } from "@repo/app-types/entities/playbook";
import type { ComponentType, FC } from "react";
import { Navigate, Outlet, type RouteObject } from "react-router";
import { appPaths } from "../../app/appPaths.js";
import { defineHandle } from "../../app/navigation/appRouteHandle.js";
import { SettingsNavigationMenu } from "../../app/navigation/menu/settingsNavigationMenu.js";
import { requireRouteParam } from "../../app/routeParams.js";
import { ParamsRedirect } from "../../app/routes/compatRedirects.js";
import { PlaybookEditPage } from "../../components/playbookEditPage.js";
import { PlaybookEditorPage } from "../../components/playbookEditorPage.js";
import { PlaybookFeedbackPage } from "../../components/playbookFeedbackPage.js";
import { PlaybookSettingsPage } from "../../components/playbookSettingsPage.js";
import { SignalNameCrumbLabel } from "../signals/signalNameCrumbLabel.js";
import { PlaybookNameCrumbLabel } from "../teammatePerformance/performanceCrumbLabels.js";
import { BillingSettingsGate } from "./billingSettings/billingSettingsGate.js";
import { CallRecorderSettingsSection } from "./callRecorderSettings/callRecorderSettingsSection.js";
import { KnowledgeBaseDetailsPage } from "./callRecorderSettings/knowledgeBaseDetailsPage.js";
import {
  KnowledgeBaseAttachmentNameCrumbLabel,
  KnowledgeBaseNameCrumbLabel,
} from "./callRecorderSettings/knowledgeBaseNameCrumbLabel.js";
import { KnowledgeBasesSettingsSection } from "./callRecorderSettings/knowledgeBasesSettingsSection.js";
import { PlaybooksSettingsSection } from "./callRecorderSettings/playbooksSettingsSection.js";
import CompanySettingsSection from "./companySettings/companySettingsSection.js";
import { DataExtractionSettingsSection } from "./dataExtractionSettings/dataExtractionSettingsSection.js";
import { DeveloperSettingsSection } from "./developerSettings/developerSettingsSection.js";
import { GenesysIntegrationDetailSection } from "./integrationSettings/genesysIntegrationDetailSection.js";
import { GenesysRecordingImportsTab } from "./integrationSettings/genesysRecordingImportsTab.js";
import { GenesysSettingsTab } from "./integrationSettings/genesysSettingsTab.js";
import { IntegrationNameCrumbLabel } from "./integrationSettings/integrationNameCrumbLabel.js";
import { IntegrationSettingsSection } from "./integrationSettings/integrationSettingsSection.js";
import { RecordingS3IntakeImportsTab } from "./integrationSettings/recordingS3IntakeImportsTab.js";
import { RecordingS3IntakeIntegrationDetailSection } from "./integrationSettings/recordingS3IntakeIntegrationDetailSection.js";
import { RecordingS3IntakeSettingsTab } from "./integrationSettings/recordingS3IntakeSettingsTab.js";
import { ZoomContactCenterImportsTab } from "./integrationSettings/zoomContactCenterImportsTab.js";
import { ZoomContactCenterIntegrationDetailSection } from "./integrationSettings/zoomContactCenterIntegrationDetailSection.js";
import { ZoomContactCenterSettingsTab } from "./integrationSettings/zoomContactCenterSettingsTab.js";
import { KeywordTrackersSettingsSection } from "./keywordTrackersSettingsSection.js";
import { MessageTemplateSettingsSection } from "./messageTemplateSettings/messageTemplateSettingsSection.js";
import { PreInterviewFormSettingsSection } from "./preInterviewFormSettings/preInterviewFormSettingsSection.js";
import ProfileSettingsSection from "./profileSettingsSection.js";
import { RecruitingIntegrationSettingsSection } from "./recruitingIntegrationSettings/recruitingIntegrationSettingsSection.js";
import { SettingsSectionGuard } from "./settingsSectionGuard.js";
import { settingsSections } from "./settingsSections.js";
import {
  useSettingsSectionsPolicy,
  type SettingsSectionKey,
} from "./settingsSectionsPolicy.js";
import { SignalListPage } from "./signalSettings/signalListPage.js";
import { SignalEditorRoute } from "./signalSettings/signalSettingsSection.js";
import {
  SignInSettingsRoute,
  TeammateGroupsSettingsRoute,
  TeammatesSettingsSection,
} from "./teammateSettings/teammateSettingsSection.js";
import { TeammatesTab } from "./teammateSettings/teammatesTab.js";
import { UserCustomFieldSettingsSection } from "./userCustomFieldSettings/userCustomFieldSettingsSection.js";
import { WebhooksSettingsSection } from "./webhooksSettings/webhooksSettingsSection.js";

// Recruiting companies get the recruiting integrations page; sales performance
// companies get the sales integrations pages (the guard has already redirected
// anyone who should see neither).
const IntegrationsSettingsSectionRoute: FC = () => {
  const { usesRecruitingIntegrations } = useSettingsSectionsPolicy();

  return usesRecruitingIntegrations ? (
    <RecruitingIntegrationSettingsSection />
  ) : (
    <IntegrationSettingsSection />
  );
};

function buildSettingsSectionRoute(
  path: string,
  section: SettingsSectionKey,
  Section: ComponentType,
): RouteObject {
  return {
    path,
    element: (
      <SettingsSectionGuard section={section}>
        <Section />
      </SettingsSectionGuard>
    ),
    handle: defineHandle({
      crumbs: () => [{ label: settingsSections[section].label }],
    }),
  };
}

/**
 * The `/settings` subtree for the data router. The conditional visibility
 * that used to decide which descendant routes were registered is enforced at
 * render time by SettingsSectionGuard. A factory (rather than a shared
 * constant) because both routers include the subtree.
 */
export function createSettingsRoute(): RouteObject {
  return {
    path: "/settings",
    // No crumb of its own: the trail starts at the section selected in the
    // left nav, never the workspace.
    handle: defineHandle({ navigationMenu: <SettingsNavigationMenu /> }),
    children: [
      { index: true, element: <Navigate to="profile" replace /> },
      {
        path: "profile",
        Component: ProfileSettingsSection,
        handle: defineHandle({
          crumbs: () => [{ label: settingsSections.profile.label }],
        }),
      },
      buildSettingsSectionRoute(
        "organization",
        "organization",
        CompanySettingsSection,
      ),
      {
        path: "teammates",
        element: (
          <SettingsSectionGuard section="teammates">
            <TeammatesSettingsSection />
          </SettingsSectionGuard>
        ),
        handle: defineHandle({
          crumbs: ({ pathname }) => [
            { label: settingsSections.teammates.label, href: pathname },
          ],
        }),
        children: [
          {
            index: true,
            Component: TeammatesTab,
          },
          {
            path: "groups",
            Component: TeammateGroupsSettingsRoute,
            handle: defineHandle({
              crumbs: () => [{ label: settingsSections.teammateGroups.label }],
            }),
          },
          {
            path: "sign-in",
            Component: SignInSettingsRoute,
            handle: defineHandle({
              crumbs: () => [{ label: settingsSections.signIn.label }],
            }),
          },
          {
            path: "*",
            element: (
              <Navigate to={appPaths.settingsTeammates.build()} replace />
            ),
          },
        ],
      },
      buildSettingsSectionRoute(
        "keyword-trackers",
        "keywordTrackers",
        KeywordTrackersSettingsSection,
      ),
      buildSettingsSectionRoute(
        "forms",
        "forms",
        PreInterviewFormSettingsSection,
      ),
      {
        path: "message-templates",
        element: (
          <SettingsSectionGuard section="messageTemplates">
            <MessageTemplateSettingsSection messageTemplateSectionType="interview" />
          </SettingsSectionGuard>
        ),
        handle: defineHandle({
          crumbs: () => [{ label: settingsSections.messageTemplates.label }],
        }),
      },
      buildSettingsSectionRoute(
        "data-extraction",
        "dataExtraction",
        DataExtractionSettingsSection,
      ),
      buildSettingsSectionRoute(
        "custom-fields",
        "customFields",
        UserCustomFieldSettingsSection,
      ),
      buildSettingsSectionRoute(
        "call-recorder-recording",
        "recording",
        CallRecorderSettingsSection,
      ),
      {
        path: "playbooks",
        element: (
          <SettingsSectionGuard section="playbooks">
            <Outlet />
          </SettingsSectionGuard>
        ),
        handle: defineHandle({
          crumbs: ({ pathname }) => [
            { label: settingsSections.playbooks.label, href: pathname },
          ],
        }),
        children: [
          {
            index: true,
            Component: PlaybooksSettingsSection,
          },
          {
            path: ":playbookId",
            Component: PlaybookEditorPage,
            handle: defineHandle({
              crumbs: ({ params }) => [
                {
                  label: (
                    <PlaybookNameCrumbLabel playbookId={params.playbookId} />
                  ),
                },
              ],
              // `/settings/playbooks/new` is served by this route with the
              // literal `new` in place of an id, which is the unsaved-playbook
              // case rather than a malformed descriptor.
              pageContext: ({ params }) => ({
                page: "playbookConfiguration",
                playbookId: playbookIdSchema.safeParse(params.playbookId).data,
              }),
            }),
            children: [
              {
                index: true,
                element: (
                  <ParamsRedirect
                    buildTo={(params) =>
                      appPaths.settingsPlaybookEdit.build({
                        playbookId: requireRouteParam(params, "playbookId"),
                      })
                    }
                  />
                ),
              },
              {
                path: "edit",
                Component: PlaybookEditPage,
              },
              {
                path: "feedback",
                Component: PlaybookFeedbackPage,
              },
              {
                path: "settings",
                Component: PlaybookSettingsPage,
              },
            ],
          },
        ],
      },
      {
        path: "knowledge-bases",
        element: (
          <SettingsSectionGuard section="knowledgeBases">
            <Outlet />
          </SettingsSectionGuard>
        ),
        handle: defineHandle({
          crumbs: ({ pathname }) => [
            { label: settingsSections.knowledgeBases.label, href: pathname },
          ],
        }),
        children: [
          {
            index: true,
            Component: KnowledgeBasesSettingsSection,
          },
          {
            path: ":knowledgeBaseId",
            element: <Outlet />,
            handle: defineHandle({
              crumbs: ({ params }) => [
                {
                  label: (
                    <KnowledgeBaseNameCrumbLabel
                      knowledgeBaseId={params.knowledgeBaseId}
                    />
                  ),
                  href: appPaths.settingsKnowledgeBase.build({
                    knowledgeBaseId: requireRouteParam(
                      params,
                      "knowledgeBaseId",
                    ),
                  }),
                },
              ],
            }),
            children: [
              {
                index: true,
                Component: KnowledgeBaseDetailsPage,
              },
              {
                path: "attachments/:attachmentId",
                Component: KnowledgeBaseDetailsPage,
                handle: defineHandle({
                  crumbs: ({ params }) => [
                    {
                      label: (
                        <KnowledgeBaseAttachmentNameCrumbLabel
                          knowledgeBaseId={params.knowledgeBaseId}
                          attachmentId={params.attachmentId}
                        />
                      ),
                    },
                  ],
                  pageContext: ({ params }) => ({
                    page: "knowledgeBaseDocument",
                    knowledgeBaseId: params.knowledgeBaseId,
                    attachmentId: params.attachmentId,
                  }),
                }),
              },
            ],
          },
        ],
      },
      {
        path: "signals",
        element: (
          <SettingsSectionGuard section="signals">
            <Outlet />
          </SettingsSectionGuard>
        ),
        handle: defineHandle({
          crumbs: ({ pathname }) => [
            { label: settingsSections.signals.label, href: pathname },
          ],
        }),
        children: [
          {
            index: true,
            Component: SignalListPage,
          },
          {
            path: "new",
            Component: SignalEditorRoute,
            handle: defineHandle({
              crumbs: () => [{ label: "New signal" }],
              pageContext: () => ({ page: "signalConfiguration" }),
            }),
          },
          {
            path: ":signalId",
            Component: SignalEditorRoute,
            handle: defineHandle({
              crumbs: ({ params }) => [
                { label: <SignalNameCrumbLabel signalId={params.signalId} /> },
              ],
              pageContext: ({ params }) => ({
                page: "signalConfiguration",
                signalId: params.signalId,
              }),
            }),
          },
        ],
      },
      buildSettingsSectionRoute("billing", "billing", BillingSettingsGate),
      {
        path: "integrations",
        element: (
          <SettingsSectionGuard section="integrations">
            <Outlet />
          </SettingsSectionGuard>
        ),
        handle: defineHandle({
          crumbs: ({ pathname }) => [
            { label: settingsSections.integrations.label, href: pathname },
          ],
        }),
        children: [
          {
            index: true,
            Component: IntegrationsSettingsSectionRoute,
          },
          {
            // Genesys gets its own tabbed, wider detail (Settings + Failed
            // imports) rather than the shared narrow detail view. A literal
            // path outranks `:integrationSlug`, so `/settings/integrations/
            // genesys` lands here; every other integration still matches the
            // param route below.
            path: "genesys",
            Component: GenesysIntegrationDetailSection,
            handle: defineHandle({
              crumbs: ({ pathname }) => [
                { label: "Genesys Cloud", href: pathname },
              ],
            }),
            children: [
              {
                index: true,
                Component: GenesysSettingsTab,
              },
              {
                path: "failed-imports",
                Component: GenesysRecordingImportsTab,
                handle: defineHandle({
                  crumbs: () => [{ label: "Failed imports" }],
                }),
              },
            ],
          },
          {
            // Zoom Contact Center gets its own tabbed, wider detail rather than
            // the shared narrow detail view. A literal path outranks
            // `:integrationSlug`, so `/settings/integrations/zoom-contact-center`
            // lands here; every other integration still matches the param route
            // below.
            path: "zoom-contact-center",
            Component: ZoomContactCenterIntegrationDetailSection,
            handle: defineHandle({
              crumbs: ({ pathname }) => [
                { label: "Zoom Contact Center", href: pathname },
              ],
            }),
            children: [
              {
                index: true,
                Component: ZoomContactCenterSettingsTab,
              },
              {
                path: "failed-imports",
                Component: ZoomContactCenterImportsTab,
                handle: defineHandle({
                  crumbs: () => [{ label: "Failed imports" }],
                }),
              },
            ],
          },
          {
            // "Recording S3 intake" gets its own tabbed, wider
            // detail like Genesys. A literal path outranks `:integrationSlug`, so
            // `/settings/integrations/recording-s3-intake` lands here.
            path: "recording-s3-intake",
            Component: RecordingS3IntakeIntegrationDetailSection,
            handle: defineHandle({
              crumbs: ({ pathname }) => [
                { label: "Recording S3 intake", href: pathname },
              ],
            }),
            children: [
              {
                index: true,
                Component: RecordingS3IntakeSettingsTab,
              },
              {
                path: "failed-imports",
                Component: RecordingS3IntakeImportsTab,
                handle: defineHandle({
                  crumbs: () => [{ label: "Failed imports" }],
                }),
              },
            ],
          },
          {
            path: ":integrationSlug",
            // The same section component renders here: it switches to the
            // detail view from the `integrationSlug` route param.
            Component: IntegrationsSettingsSectionRoute,
            handle: defineHandle({
              crumbs: ({ params }) => [
                {
                  label: (
                    <IntegrationNameCrumbLabel
                      integrationSlug={params.integrationSlug}
                    />
                  ),
                },
              ],
            }),
          },
        ],
      },
      buildSettingsSectionRoute(
        "developers",
        "developers",
        DeveloperSettingsSection,
      ),
      buildSettingsSectionRoute(
        "webhooks",
        "webhooks",
        WebhooksSettingsSection,
      ),
      {
        path: "*",
        element: <Navigate to={appPaths.settingsProfile.build()} replace />,
      },
    ],
  };
}
